import { useState, useEffect, useMemo } from 'react';

const CampaignBadge = ({ campaign, campaignPrice = null, product = null, allProducts = [] }) => {
    const [currentRuleIndex, setCurrentRuleIndex] = useState(0);
    
    // Check if this is a quantity_discount campaign
    const isQuantityDiscount = (campaignPrice?.campaign_type === 'quantity_discount' || campaign?.campaign_type === 'quantity_discount') && product && campaign?.quantity_rules;
    
    // Find rules for this product
    const productRules = useMemo(() => {
        if (!isQuantityDiscount) return [];
        return campaign.quantity_rules.filter(rule => 
            rule.product_id === product.id && 
            (rule.unit_price || rule.total_price || (rule.buy_quantity && rule.get_quantity))
        );
    }, [isQuantityDiscount, campaign?.quantity_rules, product?.id]);
    
    // Rotate through rules every 2.5 seconds
    useEffect(() => {
        if (productRules.length <= 1) return;
        
        const interval = setInterval(() => {
            setCurrentRuleIndex((prev) => (prev + 1) % productRules.length);
        }, 2500);
        
        return () => clearInterval(interval);
    }, [productRules.length]);
    
    if (!campaign) return null;

    // For quantity_discount, show rotating rules
    if (isQuantityDiscount && productRules.length > 0) {
        const currentRule = productRules[currentRuleIndex];
        const discountType = campaign.quantity_discount_type || 
            (currentRule.unit_price ? 'unit_price' : 
             currentRule.total_price ? 'total_price' : 
             (currentRule.buy_quantity && currentRule.get_quantity) ? 'buy_x_get_y' : null);
        
        let badgeText = '';
        let totalPrice = null;
        
        if (discountType === 'unit_price' && currentRule.unit_price) {
            totalPrice = currentRule.min_quantity * parseFloat(currentRule.unit_price);
            badgeText = `${currentRule.min_quantity} Adet ${product.name} ${totalPrice.toFixed(0)} TL`;
        } else if (discountType === 'total_price' && currentRule.total_price) {
            totalPrice = parseFloat(currentRule.total_price);
            badgeText = `${currentRule.min_quantity} Adet ${product.name} ${totalPrice.toFixed(0)} TL`;
        } else if (discountType === 'buy_x_get_y' && currentRule.buy_quantity && currentRule.get_quantity) {
            if (currentRule.unit_price) {
                totalPrice = currentRule.buy_quantity * parseFloat(currentRule.unit_price);
                badgeText = `${currentRule.buy_quantity}+${currentRule.get_quantity} ${product.name} ${totalPrice.toFixed(0)} TL`;
            } else {
                badgeText = `${currentRule.buy_quantity}+${currentRule.get_quantity} ${product.name}`;
            }
        }
        
        if (badgeText) {
            return (
                <span className="bg-red-500 text-white px-2 py-1 rounded text-xs font-bold shadow-md transition-opacity duration-500">
                    {badgeText}
                </span>
            );
        }
    }
    
    // Debug: Log if quantity_discount but no rules found
    if ((campaignPrice?.campaign_type === 'quantity_discount' || campaign?.campaign_type === 'quantity_discount') && product) {
        console.log('🔍 CampaignBadge Debug:', {
            campaignType: campaignPrice?.campaign_type || campaign?.campaign_type,
            hasCampaign: !!campaign,
            hasProduct: !!product,
            hasQuantityRules: !!campaign?.quantity_rules,
            quantityRulesLength: campaign?.quantity_rules?.length || 0,
            productId: product?.id,
            productRulesLength: productRules.length
        });
    }

    // Default behavior for other campaign types
    let badgeText = '';
    let badgeColor = 'bg-red-500';

    // Use campaignPrice if available (from product.campaign_prices)
    if (campaignPrice) {
        if (campaignPrice.campaign_type === 'discount') {
            if (campaignPrice.discount_type === 'percentage') {
                // Remove decimal places for percentage (e.g., 10.00 -> 10)
                const value = parseFloat(campaignPrice.discount_value);
                badgeText = `%${Math.round(value)} İndirim`;
            } else {
                badgeText = `${campaignPrice.discount_value} ₺ İndirim`;
            }
        } else if (campaignPrice.campaign_type === 'quantity_discount') {
            badgeText = 'Adet İndirimi';
            badgeColor = 'bg-red-500';
        } else if (campaignPrice.campaign_type === 'combination' || campaignPrice.campaign_type === 'combo') {
            // Kombinasyon kampanyası: Eski fiyat (kırmızı badge) + Kampanya fiyatı (yeşil badge)
            const comboPrice = parseFloat(campaignPrice.combo_price) || 0;
            const totalOriginal = campaignPrice.total_original_price;
            
            console.log('🔍 CampaignBadge - Combination campaign:', {
                campaignId: campaign?.id,
                campaignType: campaignPrice.campaign_type,
                comboPrice: comboPrice,
                totalOriginal: totalOriginal,
                willShowBadges: !!(totalOriginal && totalOriginal > comboPrice && comboPrice > 0)
            });
            
            if (totalOriginal && totalOriginal > comboPrice && comboPrice > 0) {
                return (
                    <div className="flex flex-col items-end gap-1">
                        <span 
                            className="px-2 py-1 text-xs font-bold rounded-lg line-through"
                            style={{ backgroundColor: '#fee2e2', color: '#dc2626', border: '1px solid #dc2626' }}
                        >
                            {parseFloat(totalOriginal).toFixed(0)}₺
                        </span>
                        <span 
                            className="px-2 py-1 text-sm font-bold rounded-lg"
                            style={{ backgroundColor: '#16a34a', color: '#ffffff' }}
                        >
                            {comboPrice.toFixed(0)}₺
                        </span>
                    </div>
                );
            }
            badgeText = comboPrice > 0 ? `${comboPrice.toFixed(0)}₺` : 'Kombinasyon';
            badgeColor = 'bg-green-500';
        } else if (campaignPrice.campaign_type === 'cross_product' || campaignPrice.campaign_type === 'cross_category') {
            if (campaignPrice.discount_type === 'percentage') {
                // Remove decimal places for percentage (e.g., 10.00 -> 10)
                const value = parseFloat(campaignPrice.discount_value);
                badgeText = `%${Math.round(value)} İndirim`;
            } else {
                badgeText = `${campaignPrice.discount_value} ₺ İndirim`;
            }
        }
    } else {
        // Fallback to campaign object
        if (campaign.campaign_type === 'discount' || campaign.type === 'discount') {
            const discountType = campaign.discount_type || campaign.discountType;
            const discountValue = campaign.discount_value || campaign.discountValue;
            if (discountType === 'percentage') {
                // Remove decimal places for percentage (e.g., 10.00 -> 10)
                const value = parseFloat(discountValue);
                badgeText = `%${Math.round(value)} İndirim`;
            } else {
                badgeText = `${discountValue} ₺ İndirim`;
            }
        } else if (campaign.campaign_type === 'buy_x_get_y' || campaign.type === 'buy_x_get_y') {
            const buyQty = campaign.buy_quantity || campaign.buyQuantity;
            const getQty = campaign.get_quantity || campaign.getQuantity;
            badgeText = `${buyQty}+${getQty}`;
            badgeColor = 'bg-green-500';
        } else if (campaign.campaign_type === 'quantity_discount') {
            badgeText = 'Adet İndirimi';
            badgeColor = 'bg-red-500';
        } else if (campaign.campaign_type === 'combination' || campaign.campaign_type === 'combo') {
            const comboPrice = parseFloat(campaign.combo_price) || 0;
            badgeText = comboPrice > 0 ? `${comboPrice.toFixed(0)}₺` : 'Kombinasyon';
            badgeColor = 'bg-green-500';
        } else if (campaign.campaign_type === 'cross_product' || campaign.campaign_type === 'cross_category') {
            const discountType = campaign.discount_type;
            const discountValue = campaign.discount_value;
            if (discountType === 'percentage') {
                // Remove decimal places for percentage (e.g., 10.00 -> 10)
                const value = parseFloat(discountValue);
                badgeText = `%${Math.round(value)} İndirim`;
            } else {
                badgeText = `${discountValue} ₺ İndirim`;
            }
        }
    }

    if (!badgeText) return null;

    return (
        <span className={`${badgeColor} text-white px-2 py-1 rounded text-xs font-bold shadow-md`}>
            {badgeText}
        </span>
    );
};

export default CampaignBadge;

